import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { AzureLoginService } from 'src/app/services/azure-login.service';
import { environment } from 'src/environments/environment';
import { ServiceOrderDataService } from '../../service-order-data.service';
import { ServiceOrderService } from '../../service-order.service';
import { getKeyByValue } from 'src/app/modules/crm/shared/utilities/common-utilities';
import { ApiConstants } from '../../../../shared/constants/api.constant';
@Injectable({
  providedIn: 'root',
})
export class ElectroluxTableService {
  public selectedValue = [];
  public storedValues = {};

  constructor(
    private http: HttpClient,
    private azureService: AzureLoginService,
    private serviceOrderDataService: ServiceOrderDataService,
    private serviceOrderService: ServiceOrderService,
    private apollo: Apollo,
  ) { }

  private resolveElectroluxProductLine(): string {
    const { productInfoModel } = this.serviceOrderDataService.serviceOrderDataModel;
    const { electroluxProductLines } = this.serviceOrderDataService.electroluxModal;
    if (!productInfoModel) {
      return '';
    }
    const fromSubcat = getKeyByValue(electroluxProductLines, productInfoModel.subcategoryCode);
    if (fromSubcat && fromSubcat.length > 0) {
      return fromSubcat;
    }
    const fromTier = getKeyByValue(electroluxProductLines, productInfoModel.tierDescription);
    if (fromTier && fromTier.length > 0) {
      return fromTier;
    }
    return productInfoModel.subcategoryCode
      ? productInfoModel.subcategoryCode.replace(/-/g, '').slice(0, 5)
      : '';
  }

  getAppointmentSearch(serviceOrderNumber: string, noDays: string = '14'): Observable<any> {
    const headers = {
      'content-type': 'application/json',
      Authorization: `Bearer ${this.azureService.accessToken}`,
    };

    const productLine = this.resolveElectroluxProductLine();
    const payload = {
      serviceOrderNumber: serviceOrderNumber || '',
      noDays: noDays || '14',
      servicer_type: 'electrolux',
      insertUserName: this.azureService.fullName || '',
      ...(productLine ? { productLine } : {}),
    };

    const response = this.http.post(
      `${environment.baseScmApiUrl}/${ApiConstants.electrolux.availability}`,
      JSON.stringify(payload),
      { headers }
    );

    return response;
  }

  bookAppointment(selectedValues: any, isReschedule: boolean, isCancel: boolean): Observable<any> {
    const headers = {
      'content-type': 'application/json',
      Authorization: `Bearer ${this.azureService.accessToken}`,
    };

    const { claimData } = this.serviceOrderDataService.serviceOrderDataModel;

    if (claimData.claimStatusDescription === 'PENDING DISPATCH' || claimData.claimStatusDescription === 'SERVICER REJECTED') {
      isReschedule = true;
      isCancel = true;
    }


    const readEluxWorkorderNo = (details: any): string => {
      // Same resolution order as onElxCancelConfirmation appointmentId so reschedule payload.id
      // matches the id sent to cancelElectroluxAppointment.
      const modal = this.serviceOrderDataService.electroluxModal;
      const ebs = modal.ebsDispatchNo;
      if (ebs != null && String(ebs).trim() !== '') {
        return String(ebs);
      }
      const stored = modal.eluxWorkorderNo;
      if (stored) {
        return String(stored);
      }

      const fromElectroluxResponse =
        details?.electroluxResponse?.CallStatus?.EluxWorkorderNo ||
        details?.electroluxResponse?.CallStatus?.ELUXWorkorderNo ||
        details?.CallStatus?.EluxWorkorderNo ||
        details?.CallStatus?.ELUXWorkorderNo ||
        '';
      if (fromElectroluxResponse) {
        return String(fromElectroluxResponse);
      }

      const sched = modal.scheduleCallDetails as {
        appointmentsIdOriginal?: string | number;
        appointmentsId?: string | number;
      } | null;
      const fromSchedule = sched?.appointmentsIdOriginal ?? sched?.appointmentsId;
      if (fromSchedule != null && String(fromSchedule).trim() !== '') {
        return String(fromSchedule);
      }

      if (!details?.spStatusDetails) {
        return '';
      }
      try {
        const tree =
          typeof details.spStatusDetails === 'string'
            ? JSON.parse(details.spStatusDetails)
            : details.spStatusDetails;
        return tree?.CallStatus?.EluxWorkorderNo || '';
      } catch {
        return '';
      }
    };

    const { elxCallStatusDetails } = this.serviceOrderDataService.electroluxModal;
    const eluxWorkorderNo = readEluxWorkorderNo(elxCallStatusDetails);
    const soNumber = claimData?.serviceOrderNumber || '';

    const postBook = (workNo: string): Observable<any> => {
      const scheduledDate = selectedValues?.availableDate || selectedValues?.scheduledDate || null;
      const timePeriod = selectedValues?.timePeriod || null;
      const offerToken = selectedValues?.offerToken || null;
      const groupNumber = selectedValues?.groupNumber || '';
      const productLine = this.resolveElectroluxProductLine();
      const servicerProfilesIdOriginal =
        this.serviceOrderService.selectedServicerValue?.servicerProfilesIdOriginal ??
        claimData?.servicerProfilesIdOriginal ??
        this.serviceOrderDataService.serviceOrderDataModel?.servicerInfo?.servicerProfilesIdOriginal;
      const payload: any = {
        serviceOrderNumber: claimData?.serviceOrderNumber || '',
        servicerProfilesIdOriginal,
        AppointmentSlots: {
          ScheduledDate: scheduledDate,
          TimePeriod: timePeriod,
          OfferToken: offerToken,
          GroupNumber: groupNumber,
        },
        servicer_type: 'electrolux',
        isReschedule: isReschedule,
        isCancel: isCancel,
        insertUserName: this.azureService.fullName || '',
        ...(productLine ? { productLine } : {}),
      };
      if (isReschedule && workNo && !isCancel) {
        payload.id = workNo;
      }
      return this.http.post(
        `${environment.baseScmApiUrl}/${ApiConstants.electrolux.schedule}`,
        JSON.stringify(payload),
        { headers },
      );
    };

    return postBook(eluxWorkorderNo);
  }

  cancelAppointment(appointmentId: string, cancellationReason: string): Observable<any> {
    const headers = {
      'content-type': 'application/json',
      Authorization: `Bearer ${this.azureService.accessToken}`,
    };

    const payload = {
      id: appointmentId || '',
      cancellationReason: cancellationReason || '',
      servicer_type: 'electrolux',
      insertUserName: this.azureService.fullName || '',
    };

    const response = this.http.post(
      `${environment.baseScmApiUrl}/${ApiConstants.electrolux.cancellation}`,
      JSON.stringify(payload),
      { headers }
    );

    return response;
  }

  getAppointmentStatus(serviceOrderNumber: string): Observable<any> {
    const headers = {
      'content-type': 'application/json',
      Authorization: `Bearer ${this.azureService.accessToken}`,
    };

    const payload = {
      serviceOrderNumber: serviceOrderNumber || '',
      servicer_type: 'electrolux',
      insertUserName: this.azureService.fullName || '',
    };

    const response = this.http.post(
      `${environment.baseScmApiUrl}/${ApiConstants.electrolux.statusEquiry}`,
      JSON.stringify(payload),
      { headers }
    );

    return response;
  }
}
